import React from 'react'
import { Collapse, Alert, Button, Row, Col } from 'reactstrap'

import { UsersApi, LoginApi } from 'typescript-axios'
import { compressImage, firstLetter } from 'common'
import { useRouter } from 'next/router'
import cloneDeep from 'lodash/cloneDeep'
import classNames from 'classnames'
import { getAxiosParams } from '../utils/client'
import ListingForm from './ListingForm'
import formDataJson from '../data/signup.json'
import formDataJsonGuru from '../data/signup-guru.json'
import formDataJsonGuruSso from '../data/signup-guru-sso.json'
import * as fbq from '../lib/fpixel'

import useTranslation from '../hooks/UseTranslation'
import { UserContext } from '../contexts/UserContext'
import { FormProvider } from './FormContext'
import FacebookLogin from './FacebookLogin'
import { fbLogout } from '../utils/session'

const LOGIN_SERVICE = new LoginApi(getAxiosParams())
const USER_SERVICE = new UsersApi(getAxiosParams())

const FORM_DATA_ORG = cloneDeep(formDataJson)
const FORM_DATA_ORG_GURU = cloneDeep(formDataJsonGuru)
const FORM_DATA_ORG_GURU_SSO = cloneDeep(formDataJsonGuruSso)

const Signup = (props) => {
  const { t } = useTranslation()
  const [isGuru] = React.useState(props.isGuru)
  const [isDemo] = React.useState(props.isDemo)
  const [provider, setProvider] = React.useState(null)
  const [formData, setFormData] = React.useState(null)
  const [token, setToken] = React.useState(null)
  const [error, setError] = React.useState(null)
  const [formDefaults, setFormDefaults] = React.useState({
    country_of_residence: 'FR',
    nationality: 'FR'
  })
  const [isLoading, setIsLoading] = React.useState(false)
  const { loggedUser, setLoggedUser, guruCode } = React.useContext(UserContext)
  const md = props.md ?? '6'

  const router = useRouter()

  const formatGroupLabel = (data) => (
    <h6 className='subtitle text-primary'>
      <span>{data.label}</span>
      {/* <span style={groupBadgeStyles}>{data.options.length}</span> */}
    </h6>
  )

  React.useEffect(() => {
    if (isGuru === null) {
      setFormData(null)
    } else if (isGuru) {
      const optionsActivity = []
      const perParent = {}
      props.activities.map((activity) => {
        const opt = { label: firstLetter(activity.name), value: activity.id }
        if (perParent[activity.parent]) {
          perParent[activity.parent].push(opt)
        } else {
          perParent[activity.parent] = [opt]
        }
      })
      Object.keys(perParent)
        .sort((a, b) => perParent[a] > perParent[b])
        .map((parentActivity) =>
          optionsActivity.push({
            label: firstLetter(t(`${parentActivity}_plural`)),
            options: perParent[parentActivity]
          })
        )

      let _formData
      let activityInput
      if (provider == 'email') {
        _formData = [...FORM_DATA_ORG_GURU]
        activityInput = _formData[0].formBlocks[0].inputs[4]
      } else {
        _formData = [...FORM_DATA_ORG_GURU_SSO]
        activityInput = _formData[0].formBlocks[0].inputs[3]
      }
      activityInput.options = optionsActivity
      activityInput.formatGroupLabel = formatGroupLabel
      setFormData(_formData)
    } else {
      setFormData([...FORM_DATA_ORG])
    }
  }, [isGuru, provider])

  const upload = async (formInputs) => {
    setIsLoading(true)

    try {
      const country_of_residence = isGuru
        ? formInputs.country_of_residence
        : 'FR'
      const nationality = isGuru ? formInputs.nationality : 'FR'
      const data = {
        firstname: formInputs.fname,
        lastname: formInputs.lname,
        email: formInputs.email,
        referrer_email: formInputs.referrer_email,
        password: formInputs.password,
        country_of_residence,
        nationality,
        token
      }
      data.phone_number = formInputs.phone || null
      if (isGuru) {
        const activities = []
        formInputs.activity.map((activity) => activities.push(activity.value))
        data.activity = activities
      }



      fbq.event('Lead', { content_category: isGuru ? 'GURU' : 'USER' })

      let res
      if (provider == 'email') {
        res = await USER_SERVICE.createUserApiV1UsersPost(data).catch(
          (e) => e.response
        )
      } else {
        res = await USER_SERVICE.createUserSsoFacebookApiV1UsersSsoFacebookPost(
          data
        ).catch((e) => e.response)
      }

      if (res.status < 300 && res.status >= 200) {
        let user = res.data
        let loginRes
        if (provider == 'email') {
          loginRes =
            await LOGIN_SERVICE.loginAccessTokenApiV1LoginAccessTokenPost(
              formInputs.email,
              formInputs.password
            )
        } else {
          loginRes =
            await LOGIN_SERVICE.loginAccessTokenSsoApiV1LoginAccessTokenSsoPost(
              'facebook',
              token
            )
        }
        if (loginRes.status < 300 && loginRes.status >= 200) {
          if (formInputs.files && formInputs.files.length > 0) {
            const compressed = await compressImage(formInputs.files[0], 1.0)
            const photoRes =
              await USER_SERVICE.uploadProfilePhotoApiV1UsersMeProfilePhotoPost(
                compressed
              )
            if (photoRes.status < 300 && photoRes.status >= 200) {
              user = photoRes.data
            }
          }
          setLoggedUser(user)
          props.onSuccess()
        } else {
          const msg =
            typeof loginRes.data.detail === 'string'
              ? loginRes.data.detail
              : t('account_created_modal_error')
          props.onFailure ? props.onFailure(msg) : setError(msg)
        }
      } else {
        const msg =
          typeof res.data.detail === 'string'
            ? res.data.detail
            : t('account_created_modal_error')
        props.onFailure ? props.onFailure(msg) : setError(msg)
      }
    } catch (e) {
      console.error(`failed upload ${e}`)
    }
    setIsLoading(false)
  }

  const handleLoginResponseOk = (data) => {
    setIsLoading(true)
    if (isGuru) {
      // for guru still need to provide phone number etc
      // will call upload eventually
      setToken(data.token)
      setFormDefaults({
        ...formDefaults,
        email: data.email,
        fname: data.firstname,
        lname: data.lastname
      })
      setProvider('facebook')
      setIsLoading(false)
    } else {
      // not guru simply log the user in
      USER_SERVICE.readUserMeApiV1UsersMeGet()
        .then((res) => {
          setLoggedUser(res.data)
          setIsLoading(false)
          props.onSuccess()
        })
        .catch((e) => {
          if (e.response.status == 401 || e.response.status == 404) {
            logout(setLoggedUser)
            fbLogout()
            setIsLoading(false)
          }
        })
    }
  }

  if (provider == null) {
    return (
      <>
        <Row className='pt-4'>
          <Col md={md} className={classNames('mb-2', { 'mb-lg-0': md != 12 })}>
            <FacebookLogin
              className='h-100'
              text={t('signup_with_facebook')}
              disabled={isLoading}
              onSuccess={() => {
                // account already exists
                setIsLoading(true)
                USER_SERVICE.readUserMeApiV1UsersMeGet().then((res) => {
                  setLoggedUser(res.data)
                  setIsLoading(false)
                  props.onSuccess(false) // accountCreated = false
                })
              }}
              isSignUp
              onSuccessAccountCreated={handleLoginResponseOk}
              isGuru={isGuru}
              onError={(res) => {
                fbLogout()
                setError(res.data.detail ?? t('login_error'))
                setIsLoading(false)
              }}
            />
          </Col>
          <Col md={props.md}>
            <Button
              block
              color='negative-primary'
              className='h-100'
              onClick={() => setProvider('email')}
            >
              <i className='far fa-envelope mr-2' />
              {t('signup_with_email')}
            </Button>
          </Col>
        </Row>
        <Collapse isOpen={error}>
          <Alert color='danger' className='mt-2 text-center'>
            {error}
          </Alert>
        </Collapse>
        <Row className='form-block flex-column flex-sm-row'>
          <Col className='text-center text-sm-left'>
            <Button onClick={props.back} color='link' className='text-muted'>
              <i className='fa-chevron-left fa mr-2' />
              {t('back')}
            </Button>
          </Col>
        </Row>
      </>
    )
  }
  
  return (
    <>
      {formData && (
        <div>
          <FormProvider>
            <ListingForm
              className={props.className}
              hideLeft
              defaults={formDefaults}
              disabled={isLoading}
              prevStep={props.back}
              data={formData[0]}
              finish={(inputs) => upload(inputs)}
            />
          </FormProvider>
        </div>
      )}
      <Collapse isOpen={error}>
        <Alert color='danger' className='mt-2 text-center'>
          {error}
        </Alert>
      </Collapse>
      <div
        className='mt-2 text-right'
        dangerouslySetInnerHTML={{ __html: t('signup_accepts_terms') }}
      />
    </>
  )
}

export default Signup
